import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import _ from "lodash";
import SwipeableViews from "react-swipeable-views";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Theme } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import ArrowRightAltIcon from "@material-ui/icons/ArrowRightAlt";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import challengeImage from "assets/images/challenge.png";
import Breakpoints from "common/breakpoints";
import LogoIcon from "components/Icons/SapLogo";
import CalBredSired from "components/Icons/CalBredSired";
import CustomRaceIcon from "components/Icons/CustomRace";
import LastRaceIcon from "components/Icons/LastRace";
import MatchesCount from "components/MatchesCount";
import IConditionBookPageState from "interfaces/ConditionBookPageState";
import Matches from "./Matches";
import MatchesDetails from "./MatchesDetails";

import { SHORT_DISTANCE_THRESHOLD_IN_FURLONGS } from "utils/distance-utils";
import {
  REGION_SOUTHERN_CALIFORNIA,
  REGION_NORTHERN_CALIFORNIA,
  REGION_SOUTH_FLORIDA
} from "utils/regions";
import { ageTitle, getActiveAccordionForIndex } from "../helper";
import { SortOrder } from "interfaces/SortOrder";
import {
  IConditionBookProps,
  CustomDistance,
  Distance,
  TabsValues
} from "../interfaces";
import useStyles from "./styles";

const ConditionBook = (props: IConditionBookProps) => {
  const { bookData, isShowConditionsMatches } = props;
  const classes = useStyles();
  const raceRef = useRef<HTMLDivElement>(null);
  const innerListRef = useRef<HTMLDivElement>(null);
  const currentRaceId = useSelector(
    (state: { conditionBookPage: IConditionBookPageState }) =>
      state?.conditionBookPage?.selectedRaceId
  );

  const [selectedTab, setSelectedTab] = useState<string>(TabsValues.Index);
  const [isBlockedAutoScroll, setIsBlockedAutoScroll] =
    useState<boolean>(false);
  const [selectedRaceId, setSelectedRaceId] = useState<string>(
    currentRaceId || ""
  );
  const [expandedState, setExpandedState] = useState({
    Open: [],
    "Fillies and Mares": []
  });
  const [daysExpandedState, setDaysExpandedState] = useState([]);

  useEffect(() => {
    if (selectedRaceId) {
      const { genderGroup, ageGroup } = getActiveAccordionForIndex(
        selectedRaceId,
        bookData.index
      );

      if (Boolean(genderGroup) && Boolean(ageGroup)) {
        changeAgeGroup(true, genderGroup, ageGroup);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedRaceId && innerListRef?.current && !isBlockedAutoScroll) {
      const scrollPosition = raceRef?.current?.offsetTop;

      setTimeout(
        () =>
          innerListRef?.current.scrollBy({
            top: scrollPosition,
            behavior: "smooth"
          }),
        700
      );
    }
  }, [currentRaceId, selectedRaceId, innerListRef]);

  const isScreenSM = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(Breakpoints.SM_600)
  );

  const updateDaysExpandedState = (entriesClose, expanded) => {
    setDaysExpandedState(
      expanded
        ? [...daysExpandedState, entriesClose]
        : daysExpandedState.filter(it => it !== entriesClose)
    );
  };

  const handleTabChange = (
    event: React.ChangeEvent<{ value: string }>,
    value: string
  ) => {
    event.preventDefault();
    setSelectedTab(value);
  };

  const handleChangeAgeGroup = (genderGroup, ageGroup) => (event, expanded) => {
    changeAgeGroup(expanded, genderGroup, ageGroup);
  };

  const changeAgeGroup = (expanded, genderGroup, ageGroup) => {
    const newExpandedState = {
      ...expandedState,
      [genderGroup]: expanded
        ? [...expandedState[genderGroup], ageGroup]
        : expandedState[genderGroup].filter(grName => grName !== ageGroup)
    };

    setExpandedState(newExpandedState);
  };

  const handleGroupExpand = genderGroup => () => {
    const possibleAgeGroups = Object.keys(bookData.index[genderGroup]).filter(
      key => Boolean(bookData.index[genderGroup][key].length)
    );

    if (expandedState[genderGroup].length > 0) {
      setExpandedState({
        ...expandedState,
        [genderGroup]: []
      });
    } else {
      setExpandedState({
        ...expandedState,
        [genderGroup]: possibleAgeGroups
      });
    }
  };

  const handleRaceClick = (raceId: string) => () => {
    setIsBlockedAutoScroll(true);
    setSelectedRaceId(selectedRaceId === raceId ? "" : raceId);
  };

  const handleCloseRaceDetails = () => {
    setSelectedRaceId("");
  };

  const renderRightPartPlaceholder = () => {
    return (
      <div className={classes.rightPartPlaceholder}>
        <LogoIcon className={classes.logoIcon} />
        <div className={classes.rightPartPlaceholderText}>
          Select a race to view more information
        </div>
      </div>
    );
  };

  const groupByDistance = group => {
    const result = group.reduce((result, it) => {
      if (result[it.code]) {
        result[it.code] = [...result[it.code], it];
      } else {
        result[it.code] = [it];
      }
      return result;
    }, {});

    const groupedRaceDates = Object.keys(result).map(it => {
      const item = result[it];
      if (item.length === 1) {
        return item[0];
      }
      return {
        ...item[0],
        distances: item.map(dist => {
          return {
            id: dist.id,
            raceDate: dist.raceDate,
            distance: dist.distance,
            surface: dist.surface,
            matchedHorses: dist.matchedHorses || []
          };
        })
      };
    });

    return _.orderBy(groupedRaceDates, ["code"], [SortOrder.ASC]);
  };

  const renderDistances = (
    distances: CustomDistance[] | racehorse360.RaceCondition,
    distKey: Distance
  ) => {
    const renderItem = item => {
      const matchCount = item.matchedHorses?.length || 0;

      return (
        <div
          key={item.id}
          className={clsx(classes.indexDistanceValue, {
            ["selected"]: item.id === selectedRaceId,
            ["trainerIndexDistanceValue"]: isShowConditionsMatches
          })}
          ref={item.id === selectedRaceId ? raceRef : null}
          onClick={handleRaceClick(item.id)}
        >
          {isShowConditionsMatches && (
            <MatchesCount
              matchedHorsesCount={matchCount}
              isSelected={item.id === selectedRaceId}
            />
          )}
          <span>
            {format(parseISO(item.raceDate), "MMM d")}
            {item.surface ===
              racehorse360.CourseSurface.COURSE_SURFACE_ALL_WEATHER_TRACK &&
              "(E)"}
            {(item.surface === racehorse360.CourseSurface.COURSE_SURFACE_TURF ||
              item.surface ===
                racehorse360.CourseSurface.COURSE_SURFACE_DOWNHILL_TURF) &&
              "(T)"}
          </span>
        </div>
      );
    };

    if (Array.isArray(distances)) {
      const sorted = _.orderBy(distances, ["raceDate"]);
      return sorted
        .filter(it => {
          if (distKey === Distance.short) {
            return it.distance < SHORT_DISTANCE_THRESHOLD_IN_FURLONGS;
          } else {
            return it.distance >= SHORT_DISTANCE_THRESHOLD_IN_FURLONGS;
          }
        })
        .map(renderItem);
    } else {
      if (
        (distKey === Distance.short &&
          distances.distance < SHORT_DISTANCE_THRESHOLD_IN_FURLONGS) ||
        (distKey === Distance.long &&
          distances.distance >= SHORT_DISTANCE_THRESHOLD_IN_FURLONGS)
      )
        return renderItem(distances);
    }
  };
  const renderBookIndex = () => {
    if (!bookData) return null;

    return (
      <div className={clsx(classes.innerList)} ref={innerListRef}>
        {Object.keys(bookData.index)
          .map(genderGroup => {
            const shouldGenderGroupDisplays = Object.keys(
              bookData.index[genderGroup]
            ).some(subgroup => {
              return bookData.index[genderGroup][subgroup].length > 0;
            });

            if (!shouldGenderGroupDisplays) return null;

            return (
              <div key={genderGroup} className={classes.genderGroup}>
                <div className={classes.genderGroupHeader}>
                  <div className={classes.genderGroupHeaderTitle}>
                    {genderGroup}
                  </div>
                  <div
                    className={classes.genderGroupHeaderExpandAll}
                    onClick={handleGroupExpand(genderGroup)}
                  >
                    {expandedState[genderGroup].length === 0
                      ? "Expand All"
                      : "Collapse All"}
                  </div>
                </div>
                <div className={classes.genderGroupBody}>
                  {Object.keys(bookData.index[genderGroup])
                    .filter(
                      group => bookData.index[genderGroup][group].length > 0
                    )
                    .map(ageGroup => {
                      return (
                        <Accordion
                          key={`${genderGroup}_${ageGroup}`}
                          className={clsx(classes.accordion, "indexAccordion")}
                          classes={{
                            root: classes.expRoot,
                            disabled: classes.expDisabled
                          }}
                          expanded={expandedState[genderGroup].includes(
                            ageGroup
                          )}
                          onChange={handleChangeAgeGroup(genderGroup, ageGroup)}
                        >
                          <AccordionSummary
                            className={classes.accordionSummary}
                            expandIcon={<ExpandMoreIcon />}
                            classes={{
                              root: classes.sumRoot,
                              expanded: classes.sumRootExpanded
                            }}
                          >
                            <div className={classes.summaryContent}>
                              <div className={classes.summaryTitle}>
                                {isShowConditionsMatches && (
                                  <MatchesCount
                                    matchedHorsesCount={countGroupMatches(
                                      genderGroup,
                                      ageGroup
                                    )}
                                  />
                                )}
                                <div
                                  className={clsx(classes.groupName, {
                                    ["trainerGroupName"]:
                                      isShowConditionsMatches
                                  })}
                                >
                                  {ageGroup}
                                </div>
                              </div>
                            </div>
                          </AccordionSummary>
                          {expandedState[genderGroup].includes(ageGroup) && (
                            <AccordionDetails
                              className={classes.accordionDetails}
                            >
                              {groupByDistance(
                                bookData.index[genderGroup][ageGroup]
                              ).map(raceData => {
                                const matchedHorses = [];

                                if (raceData.distances) {
                                  raceData.distances.forEach(
                                    (distance: CustomDistance) => {
                                      matchedHorses.push(
                                        ...distance.matchedHorses
                                      );
                                    }
                                  );
                                } else {
                                  raceData.matchedHorses &&
                                    matchedHorses.push(
                                      ...raceData.matchedHorses
                                    );
                                }

                                const isShowLastRaceIcon =
                                  matchedHorses.some(
                                    (horse: racehorse360.IHorse) => {
                                      return horse.areLastRacePreferredConditionsSelected;
                                    }
                                  ) && isShowConditionsMatches;
                                const isShowCustomRaceIcon =
                                  matchedHorses.some(
                                    (horse: racehorse360.IHorse) => {
                                      return !horse.areLastRacePreferredConditionsSelected;
                                    }
                                  ) && isShowConditionsMatches;

                                return (
                                  <div
                                    key={`${genderGroup}_${ageGroup}_${raceData.id}`}
                                    className={clsx(classes.indexRaceData)}
                                  >
                                    <div
                                      className={classes.indexRaceDataHeader}
                                    >
                                      <span>{raceData.code}</span>
                                      <div className={classes.conditionsIcons}>
                                        {isShowLastRaceIcon && (
                                          <LastRaceIcon
                                            className={classes.lastRaceIcon}
                                          />
                                        )}
                                        {isShowCustomRaceIcon && (
                                          <CustomRaceIcon
                                            className={classes.customRaceIcon}
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <div className={classes.indexRaceDataBody}>
                                      <div
                                        className={
                                          classes.indexRaceDataDistance
                                        }
                                      >
                                        <span
                                          className={classes.indexDistanceTitle}
                                        >
                                          Short:
                                        </span>
                                        <div
                                          className={classes.distancesContainer}
                                        >
                                          {renderDistances(
                                            raceData.distances || raceData,
                                            Distance.short
                                          )}
                                        </div>
                                      </div>
                                      <div
                                        className={
                                          classes.indexRaceDataDistance
                                        }
                                      >
                                        <span
                                          className={classes.indexDistanceTitle}
                                        >
                                          Long:
                                        </span>
                                        <div
                                          className={classes.distancesContainer}
                                        >
                                          {renderDistances(
                                            raceData.distances || raceData,
                                            Distance.long
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </AccordionDetails>
                          )}
                        </Accordion>
                      );
                    })}
                </div>
              </div>
            );
          })
          .filter(Boolean)}
      </div>
    );
  };

  const getGroupedRaces = races => {
    const regular = [];
    const substitute = [];
    const extra = [];

    races.forEach(race => {
      const number = race.number;
      if (number.includes("S")) {
        const order = number.replace("S", "");
        race.customOrder = Number(order);
        substitute.push(race);
      } else if (number.includes("X")) {
        const order = number.replace("X", "");
        race.customOrder = Number(order);
        extra.push(race);
      } else {
        race.customOrder = Number(number);
        regular.push(race);
      }
    });

    return [
      ..._.orderBy(regular, ["customOrder"]),
      ..._.orderBy(substitute, ["customOrder"]),
      ..._.orderBy(extra, ["customOrder"])
    ];
  };

  const renderWeightConditionsAndClaiming = race => {
    const renderWeight = (
      label,
      value,
      isClaiming,
      key: string | number = ""
    ) => {
      return (
        <div key={key} className={classes.groups}>
          <div className={classes.raceGroup}>
            <div
              className={clsx(classes.ruleGroupName, {
                [classes.boldText]: isClaiming
              })}
            >
              {label}
            </div>
            {!isClaiming || (isClaiming && Number(value)) ? (
              <>
                <div className={classes.dots} />
                <div className={classes.groupCondition}>{`${value} lbs.`}</div>
              </>
            ) : null}
          </div>
        </div>
      );
    };

    const renderAllowance = (jsonString: string) => {
      try {
        return JSON.parse(jsonString).map((it, index) => {
          return renderWeight(it.text, it.lbs_allowed, false, index);
        });
      } catch (error) {
        return <></>;
      }
    };

    return (
      <>
        {["1", "2", "3"].map((num, index) => {
          const condition = race[`weight_${num}Condition`];
          const lbsValue = race[`weight_${num}LbsValue`]?.value;
          if (condition && lbsValue) {
            return renderWeight(condition, lbsValue, false, index);
          }
          return null;
        })}
        {renderAllowance(race.allowance)}
        {race.claimingText &&
          race.claimingStepAllowance &&
          renderWeight(race.claimingText, race.claimingStepAllowance, true)}
      </>
    );
  };

  const renderPublishedDescription = race => {
    if (!race.publishedDescriptions?.value) {
      return null;
    }

    let parsed;

    try {
      parsed = JSON.parse(race.publishedDescriptions.value);
    } catch (error) {
      parsed = [];
    }

    return (
      <>
        {parsed.map((it, i) => {
          return (
            <div className={classes.stakeRaceName} key={i}>
              {it}
            </div>
          );
        })}
      </>
    );
  };

  const renderDetailsLink = isSelectedRace => (
    <div
      className={clsx(classes.detailsLinkContainer, {
        [classes.detailsLinkContainerSelected]: isSelectedRace
      })}
    >
      <ArrowRightAltIcon className={clsx(classes.linkArrow)} />
    </div>
  );

  const renderRaceHeader = race => (
    <div className={classes.raceHeader}>
      <div className={classes.index}>
        {renderCalBredOrSiredIcon(race)}
        {race.number}
      </div>
      <div className={classes.title}>
        <div className={classes.mainTitle}>{race.numberText}</div>
        <div className={classes.subtitle}>
          {`${race.restrictionDescription || ""} ${race.raceDescription}`}
        </div>
      </div>
    </div>
  );

  const renderRaceData = (race, addMarginBottom = false) => {
    return (
      <>
        {race.raceName_1 && (
          <div className={classes.stakeRaceName}>{`${
            race.runningOfRace === "0th"
              ? race.raceName_1
              : "The " + race.runningOfRace + " Running of " + race.raceName_1
          }`}</div>
        )}
        {["2", "3", "4"].map(
          (num, i) =>
            race[`raceName_${num}`] && (
              <div className={classes.stakeRaceName} key={i}>
                {race[`raceName_${num}`]}
              </div>
            )
        )}
        {race.purse && (
          <div className={classes.stakeRaceName}>
            {`${race.purse} ${race.guaranteedIndicator || ""} ${
              race.grade
                ? race.grade === "Listed"
                  ? "Listed"
                  : "Grade " + race.grade
                : ""
            } `}
          </div>
        )}
        {renderPublishedDescription(race)}
        {(race.detailsBold || race.details) && (
          <div className={classes.stakesDetails}>
            <span className={classes.stakesBoldDetails}>
              {race.detailsBold}
            </span>
            <span className={classes.stakesRegularDetails}>{race.details}</span>
          </div>
        )}
        {race.raceCloseDate && (
          <div
            className={clsx(classes.nominationClose, {
              [classes.marginBottom]: addMarginBottom
            })}
          >
            {`Nominations Close ${format(
              parseISO(race.raceCloseDate),
              "EEEE, LLLL d, yyyy"
            )}`}
          </div>
        )}
      </>
    );
  };

  const renderCalBredOrSiredIcon = (race: racehorse360.IRaceCondition) => {
    if (
      race.restriction ===
      racehorse360.RaceConditionRestriction
        .RACE_CONDITION_RESTRICTION_STATE_BRED_OR_SIRED
    ) {
      if (
        race.facility.region.id === REGION_SOUTHERN_CALIFORNIA ||
        race.facility.region.id === REGION_NORTHERN_CALIFORNIA
      ) {
        return <CalBredSired className={classes.stateBredSiredIcon} />;
      }
      if (race.facility.region.id === REGION_SOUTH_FLORIDA) {
        // This icon needs to be replaced with Florida icon instead of California icon. Waiting for icon design
        return <CalBredSired className={classes.stateBredSiredIcon} />;
      }
    }
  };

  const renderBookConditions = () => {
    const conditionsArray = Object.keys(bookData.conditions).map(
      it => bookData.conditions[it]
    );

    const sorted = _.orderBy(conditionsArray, ["date"]);

    const list = sorted.map(it => {
      const conditionHeader = `${it.day}${it.entriesClose}`;
      const races = getGroupedRaces(it.races);
      const expanded = daysExpandedState.includes(conditionHeader);

      return (
        <div key={conditionHeader} className={classes.genderGroupBody}>
          <Accordion
            square
            className={clsx(classes.accordion, "conditionsAccordion")}
            classes={{ root: classes.expRoot, disabled: classes.expDisabled }}
            expanded={expanded}
            onChange={(event, expanded) =>
              updateDaysExpandedState(conditionHeader, expanded)
            }
          >
            <AccordionSummary
              className={classes.accordionSummary}
              expandIcon={<ExpandMoreIcon />}
              classes={{
                root: classes.sumRoot,
                expanded: classes.sumRootExpanded
              }}
            >
              <div className={classes.summaryContent}>
                <div
                  className={clsx(
                    classes.summaryTitle,
                    classes.summaryTitleConditions
                  )}
                >
                  <div className={classes.dayName}>{`${it.day}:`}</div>
                  <div className={classes.dayDate}>
                    {format(parseISO(it.date), "iiii, MMM d, yyyy")}
                  </div>
                </div>
              </div>
            </AccordionSummary>
            {expanded && (
              <AccordionDetails className={classes.accordionDetails}>
                <div className={classes.dayDetailsList}>
                  <div className={classes.dayDetail}>
                    <div className={classes.dayDetailTitle}>Entries Close:</div>
                    <div className={classes.dayDetailText}>
                      {it.entriesClose
                        ? format(parseISO(it.entriesClose), "iiii, MMM d, yyyy")
                        : "-"}
                    </div>
                  </div>
                  <div className={classes.dayDetail}>
                    <div className={classes.dayDetailTitle}>Scratch Time:</div>
                    <div className={classes.dayDetailText}>
                      {it.scratchTime
                        ? format(
                            parseISO(it.scratchTime),
                            "iiii, MMM d, yyyy 'at' hh:mm aa"
                          )
                        : "-"}
                    </div>
                  </div>
                </div>
                <div className={classes.dayRaceList}>
                  {races.map(race => {
                    const isSelectedRace = race.id === selectedRaceId;
                    const isStakeRace = race.isStake;
                    if (isStakeRace) {
                      return (
                        <div
                          key={race.id}
                          onClick={handleRaceClick(race.id)}
                          className={clsx(classes.raceContainer, {
                            [classes.raceContainerSelected]: isSelectedRace
                          })}
                        >
                          {renderDetailsLink(isSelectedRace)}
                          {renderRaceHeader(race)}
                          <div className={classes.raceBody}>
                            {renderRaceData(race)}
                          </div>
                          {isShowConditionsMatches && (
                            <Matches matchedHorses={race.matchedHorses} />
                          )}
                        </div>
                      );
                    }
                    return (
                      <div
                        key={race.id}
                        onClick={handleRaceClick(race.id)}
                        className={clsx(classes.raceContainer, {
                          [classes.raceContainerSelected]: isSelectedRace
                        })}
                      >
                        {renderDetailsLink(isSelectedRace)}
                        {renderRaceHeader(race)}
                        <div className={classes.raceBody}>
                          <div className={classes.raceDetails}>
                            {race.details}
                          </div>
                          <div className={classes.distance}>
                            <div className={classes.distanceTitle}>
                              DISTANCE:
                            </div>
                            {`${race.distanceDescription} (${race.surfaceDescription})`}
                          </div>
                          {renderWeightConditionsAndClaiming(race)}
                          {race.notConsidered && (
                            <div className={classes.preferenceText}>
                              {race.notConsidered}
                            </div>
                          )}
                          {race.preference && (
                            <div className={classes.preferenceText}>
                              {race.preference}
                            </div>
                          )}
                        </div>
                        {isShowConditionsMatches && (
                          <Matches matchedHorses={race.matchedHorses} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </AccordionDetails>
            )}
          </Accordion>
        </div>
      );
    });

    return <div className={clsx(classes.innerList)}>{list}</div>;
  };

  const renderAgeTitle = age => {
    const title = ageTitle[age];
    return title ? title.toUpperCase() : "-";
  };

  const renderRightPartContent = () => {
    const races = props.rawRaces;

    const race = races.find(race => {
      return race.id === selectedRaceId;
    });

    if (!race) {
      return renderRightPartPlaceholder();
    }

    const matchedHorses = race.matchedHorses || [];

    return (
      <div className={clsx(classes.paper, classes.rightPaper)}>
        <div className={classes.paperContent}>
          <div className={classes.raceDetailsHeader}>
            <div className={classes.raceDetailsMainHeader}>
              <div
                className={clsx(classes.raceDetailsMainTitle, {
                  [classes.longMainTitle]: race.code?.length > 25
                })}
              >
                {renderCalBredOrSiredIcon(race)}
                {race.code}
              </div>
              <div className={classes.raceDetailsSubTitle}>
                {`${race.facility.name}`}
              </div>
            </div>
            <div className={classes.raceDetailsCloseContainer}>
              <IconButton
                aria-label="close"
                onClick={handleCloseRaceDetails}
                className={clsx(classes.closeButton)}
              >
                <CloseIcon className={classes.closeButtonIcon} />
              </IconButton>
            </div>
          </div>
          <div className={classes.raceDetailsBody}>
            <div className={classes.raceDetailsBodyTop}>
              <div className={classes.raceDetailsIndexContainer}>
                <div className={classes.raceDetailsIndex}>{race.number}</div>
                <div className={classes.raceDetailsTitlesContainer}>
                  <div className={classes.raceDetailsTitlesMain}>
                    {race.numberText}
                  </div>
                  <div className={classes.raceDetailsTitlesSub}>
                    {`${race.restrictionDescription || ""} ${
                      race.raceDescription
                    }`}
                  </div>
                </div>
              </div>
              <div className={classes.raceDetailsDataContainer}>
                <div className={classes.raceDetailsRaceDate}>
                  {format(parseISO(race.raceDate), "EEEE, LLLL d")}
                </div>
                <div className={classes.raceDetailsRaceFacility}>
                  {`${race.facility.name.toLowerCase()}`}
                </div>
              </div>
            </div>
            <div className={classes.raceDetailsBodyCenter}>
              <div className={classes.regularRaceInfo}>
                {race.isChallenge && (
                  <div className={classes.challengeInfoIcon}>
                    <img
                      src={challengeImage}
                      className={classes.challengeImage}
                      alt=""
                    />
                  </div>
                )}

                {race.isStake && renderRaceData(race, true)}

                <div className={classes.challengeDetail}>
                  <div className={classes.challengeDetailTitle}>AGE:</div>
                  <div className={classes.challengeDetailValue}>
                    {renderAgeTitle(race.age)}
                  </div>
                </div>
                <div className={classes.challengeDetail}>
                  <div className={classes.challengeDetailTitle}>DISTANCE:</div>
                  <div className={classes.challengeDetailValue}>
                    {`${race.distanceDescription} (${race.surfaceDescription})`}
                  </div>
                </div>
                <div className={classes.groups}>
                  {renderWeightConditionsAndClaiming(race)}
                </div>
                {race.notConsidered && (
                  <div className={classes.preferenceText}>
                    {race.notConsidered}
                  </div>
                )}
                {race.preference && (
                  <div className={classes.preferenceText}>
                    {race.preference}
                  </div>
                )}
              </div>
            </div>
            {isShowConditionsMatches && (
              <MatchesDetails
                matchedHorses={matchedHorses}
                selectedRaceId={selectedRaceId}
              />
            )}
            <div>
              {!race.isStake && (
                <Accordion
                  square
                  className={clsx(classes.accordion, "detailsAccordion")}
                  classes={{
                    root: classes.expRoot,
                    disabled: classes.expDisabled
                  }}
                  defaultExpanded
                >
                  <AccordionSummary
                    className={classes.accordionSummary}
                    expandIcon={<ExpandMoreIcon />}
                    classes={{
                      root: classes.sumRoot,
                      expanded: classes.sumRootExpanded
                    }}
                  >
                    <div className={classes.summaryContent}>
                      <div
                        className={clsx(
                          classes.summaryTitle,
                          classes.boldText,
                          classes.normalTextSize
                        )}
                      >
                        Details
                      </div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails
                    className={clsx(
                      classes.accordionDetails,
                      classes.normalTextSize
                    )}
                  >
                    <div className={classes.raceDetailsDescription}>
                      {race.details}
                    </div>
                  </AccordionDetails>
                </Accordion>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMobileView = () => {
    return (
      <SwipeableViews
        index={selectedTab === TabsValues.Index ? 0 : 1}
        onChangeIndex={handleChangeIndex}
        className={classes.swipeContainer}
      >
        {renderBookIndex()}
        {renderBookConditions()}
      </SwipeableViews>
    );
  };

  const renderDesktopView = () => {
    return selectedTab === TabsValues.Index
      ? renderBookIndex()
      : renderBookConditions();
  };

  const countGroupMatches = (genderGroup: string, ageGroup: string): number => {
    return bookData.index[genderGroup][ageGroup].reduce((acc, cur) => {
      return cur.matchedHorses ? acc + cur.matchedHorses.length : acc + 0;
    }, 0);
  };

  const handleChangeIndex = index => {
    const tab = index === 0 ? TabsValues.Index : TabsValues.Conditions;
    setSelectedTab(tab);
  };

  return (
    <div className={clsx(classes.bookContainer)}>
      <div className={clsx(classes.paper, classes.leftPaper)}>
        <div className={classes.paperContent}>
          <Tabs
            className={clsx(classes.tabs)}
            value={selectedTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab
              className={clsx(classes.tab)}
              label="Index"
              value={TabsValues.Index}
            />
            <Tab
              className={clsx(classes.tab)}
              label="Conditions"
              value={TabsValues.Conditions}
            />
          </Tabs>
          <div className={classes.tabContent}>
            {isScreenSM && !selectedRaceId
              ? renderMobileView()
              : renderDesktopView()}
          </div>
        </div>
      </div>
      {selectedRaceId ? renderRightPartContent() : renderRightPartPlaceholder()}
    </div>
  );
};

export default ConditionBook;
